<template>
  <div v-if="message" class="alert" :class="alertType">
    <div class="d-flex align-content-center">
      <div v-if="alertIcon" class="icon my-auto">
        <i :class="alertIcon"></i>
      </div>
      <div v-if="alertImage">
        <img
          :src="useAsset(alertImage)"
          class="img-fluid img-responsive my-auto h-100"
          alt="img"
        />
      </div>
      <div
        class="message col"
        :class="{ 'fs-3': isSelfService }"
        :style="messageClampStyle"
      >
        <div v-if="header" class="mb-3">
          {{ header }}
        </div>
        <div>
          {{ message }}
        </div>
        <div v-if="callToActions !== null" class="mt-3">
          <button
            v-for="(cta, index) in callToActions"
            :key="index"
            @click="onCtaClick(cta.callback)"
            class="btn btn-success"
          >
            {{ cta.actionMessage }}
          </button>
        </div>
      </div>
      <button
        v-if="closable"
        @click="emit('onClose')"
        class="dismiss-alert-button align-self-start"
        data-cy="alert-dismiss-button"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import useAsset from "@/shared-helpers/useAsset";
import { computed, PropType } from "vue";
import {
  AlertCallToAction,
  AlertCallToActionCallback,
} from "@/types/alertCallToAction";

type alertTypeString =
  | "alert-primary"
  | "alert-secondary"
  | "alert-success"
  | "alert-danger"
  | "alert-warning"
  | "alert-info"
  | "alert-info-dark"
  | "alert-light"
  | "alert-dark";

const props = defineProps({
  alertIcon: {
    type: String,
    default: null,
  },
  alertImage: {
    type: String,
    default: null,
  },
  header: {
    type: String,
    default: null,
  },
  message: {
    type: String,
    default: null,
  },
  alertType: {
    type: String as PropType<alertTypeString>,
    default: "alert-warning",
  },
  closable: {
    type: Boolean,
    default: true,
  },
  isSelfService: {
    type: Boolean,
    default: false,
  },
  callToActions: {
    type: Array as () => AlertCallToAction[],
    default: null,
  },
  messageFontStyle: {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Object as PropType<Record<string, any>> | undefined,
    default: undefined,
  },
  messageFontWeight: {
    type: String,
    default: "bold",
  },
  messageLines: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(["onClose"]);

const onCtaClick = (callback: AlertCallToActionCallback) => {
  callback();
};

const messageClampStyle = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const base: Record<string, any> = {
    fontWeight: props.messageFontWeight,
    ...(props.messageFontStyle || {}),
  };

  if (props.messageLines && props.messageLines > 0) {
    return {
      ...base,
      maxHeight: `calc(${base.lineHeight ?? "1.2em"} * ${props.messageLines})`,
      overflow: "hidden",
      whiteSpace: "normal",
      overflowWrap: base.overflowWrap ?? "anywhere",
      wordBreak: base.wordBreak ?? "break-word",
    };
  }

  return base;
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.alert {
  padding: 1em;
  border-radius: 5px;
}

.message {
  padding: 0 10px;
  font-size: 14px;
  -webkit-text-size-adjust: none;
  -moz-text-size-adjust: none;
  -ms-text-size-adjust: none;
  text-size-adjust: none;
}

.dismiss-alert-button {
  background: none;
  border: none;
}

.alert-primary {
  border-radius: 5px;
  background: #e6f4ea;
  border: none;
  color: var(--Black, #007c3b);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400 !important;
  line-height: 17px;
}

.alert-info-dark {
  border-radius: 3px;
  background: var(--Gray-elements-Segmenter-lines, #f2f2f2) !important;
}
</style>
